// ====================================================
// src/components/block/liveStreamManagement/videoEffects/FaceTracker.ts
//
// Suivi du visage en temps réel, pour l'effet "Avatar" (cf. AvatarEffect.tsx).
// Même patron que PersonSegmenter.ts : MediaPipe Tasks Vision (WASM), modèle
// "face landmarker" hébergé par Google, chargé à la demande, singleton pour
// toute la session. Tâche MediaPipe DISTINCTE du détourage (ImageSegmenter) —
// deux modèles, deux singletons indépendants, même si le runtime WASM vient
// du même package.
//
// Différence avec ImageSegmenter.segmentForVideo (callback) : FaceLandmarker.
// detectForVideo() est SYNCHRONE — pas de Promise à l'intérieur, cf. le .d.ts
// du package. detectFaceInVideo() reste async uniquement pour attendre le
// chargement du modèle au premier appel (comme segmentPersonInVideo).
// ====================================================

import { FilesetResolver, FaceLandmarker, type FaceLandmarkerResult } from '@mediapipe/tasks-vision';
import { isFaceDetectionDue, fitWithin } from './faceTrackingBudget';

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';
// Plusieurs personnes peuvent apparaître dans le cadre d'une caméra de régie — pas
// qu'un unique présentateur cadré serré. 4 reste raisonnable en coût de calcul.
const MAX_FACES = 4;

let landmarkerPromise: Promise<FaceLandmarker> | null = null;

async function createFaceLandmarker(vision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>, delegate: 'GPU' | 'CPU'): Promise<FaceLandmarker> {
  return FaceLandmarker.createFromOptions(vision, {
    baseOptions: { modelAssetPath: MODEL_URL, delegate },
    runningMode: 'VIDEO',
    numFaces: MAX_FACES,
    // Scores 0..1 par expression (52 catégories ARKit standard) — utilisés pour
    // animer les modèles articulés (cf. AvatarModels.ts 'articulated', clignement
    // des yeux/ouverture de bouche dans AvatarRenderer.applyFaceArticulation).
    outputFaceBlendshapes: true,
    // Position/rotation de la tête — pas encore exploitée (cf. AvatarRenderer.ts,
    // qui calcule sa propre orientation depuis les repères plutôt que cette
    // matrice), mais coûte peu à demander dès maintenant.
    outputFacialTransformationMatrixes: true,
  });
}

function getFaceLandmarker(): Promise<FaceLandmarker> {
  if (!landmarkerPromise) {
    landmarkerPromise = FilesetResolver.forVisionTasks(WASM_BASE).then(async vision => {
      try {
        return await createFaceLandmarker(vision, 'GPU');
      } catch (err) {
        console.warn('[FaceTracker] délégué GPU indisponible, repli CPU :', err);
        return createFaceLandmarker(vision, 'CPU');
      }
    });
  }
  return landmarkerPromise;
}

export interface FaceLandmarkPoint { x: number; y: number; z: number; }

export interface FaceTrackingResult {
  /** 478 points normalisés (0..1 dans le cadre de la frame), pour CE visage. */
  landmarks: FaceLandmarkPoint[];
  /** Matrice 4x4 à plat (position + rotation de la tête) — pour l'ancrage d'un futur modèle 3D. */
  facialTransformationMatrix: Float32Array | null;
  /** Score 0 (pas du tout) à 1 (à fond) par expression, indexé par nom de catégorie
   * MediaPipe (ex. 'eyeBlinkLeft', 'jawOpen') — cf. getBlendshapeScore ci-dessous.
   * Vide si le modèle n'a pas encore renvoyé de blendshapes pour ce visage. */
  blendshapes: Record<string, number>;
}

/** Lecture sûre d'un score de blendshape par son nom MediaPipe — 0 si absent
 * (modèle pas encore chaud, ou catégorie inexistante) plutôt qu'undefined, pour
 * ne pas forcer chaque appelant à re-tester la nullité. */
export function getBlendshapeScore(result: FaceTrackingResult, name: string): number {
  return result.blendshapes[name] ?? 0;
}

/**
 * Détecte les visages (jusqu'à MAX_FACES) dans une frame de `video` — tableau vide si
 * aucun. `timestampMs` doit être strictement croissant entre deux appels — même
 * contrainte que segmentPersonInVideo (cf. PersonSegmenter) : un seul appelant à la fois.
 */
// Budget de calcul — cf. faceTrackingBudget.ts. Par <video> : la boucle de rendu de la
// régie et l'aperçu du réglage de source peuvent suivre deux vidéos différentes, chacune
// avec sa propre cadence et son dernier résultat.
const lastByVideo = new WeakMap<HTMLVideoElement, { at: number; result: FaceTrackingResult[] }>();
let downscaleCanvas: HTMLCanvasElement | null = null;

/** L'image donnée au détecteur : la vidéo réduite (repères normalisés, donc inchangés), ou
 *  la vidéo elle-même si la réduction est impossible. */
function detectorInput(video: HTMLVideoElement): HTMLVideoElement | HTMLCanvasElement {
  const { width, height } = fitWithin(video.videoWidth, video.videoHeight);
  if (width === video.videoWidth && height === video.videoHeight) return video;
  try {
    if (!downscaleCanvas) downscaleCanvas = document.createElement('canvas');
    if (downscaleCanvas.width !== width || downscaleCanvas.height !== height) {
      downscaleCanvas.width = width; downscaleCanvas.height = height;
    }
    const ctx = downscaleCanvas.getContext('2d');
    if (!ctx) return video;
    ctx.drawImage(video, 0, 0, width, height);
    return downscaleCanvas;
  } catch {
    return video;
  }
}

export async function detectFaceInVideo(video: HTMLVideoElement, timestampMs: number): Promise<FaceTrackingResult[]> {
  if (video.readyState < 2 || video.videoWidth === 0) return [];
  const previous = lastByVideo.get(video);
  if (previous && !isFaceDetectionDue(timestampMs, previous.at)) return previous.result;
  let landmarker: FaceLandmarker;
  try {
    landmarker = await getFaceLandmarker();
  } catch (err) {
    console.warn('[FaceTracker] échec de chargement du modèle :', err);
    return [];
  }

  try {
    const result: FaceLandmarkerResult = landmarker.detectForVideo(detectorInput(video), timestampMs);
    if (!result.faceLandmarks || result.faceLandmarks.length === 0) { lastByVideo.set(video, { at: timestampMs, result: [] }); return []; }
    const faces = result.faceLandmarks.map((landmarks, i) => {
      const matrix = result.facialTransformationMatrixes?.[i]?.data ?? null;
      const blendshapes: Record<string, number> = {};
      for (const category of result.faceBlendshapes?.[i]?.categories ?? []) {
        blendshapes[category.categoryName] = category.score;
      }
      return {
        landmarks: landmarks.map(p => ({ x: p.x, y: p.y, z: p.z })),
        facialTransformationMatrix: matrix ? Float32Array.from(matrix) : null,
        blendshapes,
      };
    });
    lastByVideo.set(video, { at: timestampMs, result: faces });
    return faces;
  } catch (err) {
    console.warn('[FaceTracker] échec de détection :', err);
    return [];
  }
}
